import { IActionResponse } from "../../dal/BaseDAL";
import api from "../Api";
import {IPreparedCart, IPreparedCartDeliveryRequest} from "../../models/PreparedCart";
import {IPreparedCartDirectLinksResponse, IStorefrontBundleEstimate} from "../../models/Storefront";
import {IWorkspaceItem} from "../../models/Workspace";
import { ICollectionResponse } from "../../models/ICollectionResponse";
import { ILookupConditions } from "../../models/Extension";

const preparedCartApi = api.injectEndpoints({
    endpoints: (build) => ({
        getPreparedCarts: build.query<ICollectionResponse<IPreparedCart>, { conditions?: ILookupConditions }>({
            query: (arg) => ({
                url: '/preparedCarts',
                method: 'GET',
                params: arg.conditions ? {
                    page: arg.conditions.page,
                    pageSize: arg.conditions.pageSize,
                    searchTerm: arg.conditions.searchTerm,
                    searchBy: arg.conditions.searchBy,
                    sortBy: arg.conditions.sortBy,
                    sortDirection: arg.conditions.sortDirection,
                } : undefined,
            }),
            serializeQueryArgs: ({ endpointName }) => endpointName,
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg;
            },
        }),
        // Per-argument-cached name search for the global search palette ("quotes"). getPreparedCarts
        // collapses all args onto one cache entry for the admin grid, so it can't be shared here.
        searchPreparedCartRefs: build.query<ICollectionResponse<IPreparedCart>, { searchTerm: string, pageSize?: number }>({
            query: (arg) => ({
                url: '/preparedCarts',
                method: 'GET',
                params: {
                    page: 1,
                    pageSize: arg.pageSize ?? 5,
                    searchTerm: arg.searchTerm,
                },
            }),
        }),
        putPreparedCart: build.mutation<IActionResponse, { request: Partial<IPreparedCart> }>({
            query: (arg) => ({
                url: `/preparedCarts`,
                method: "PUT",
                body: arg.request,
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'PreparedCartEmailBody', id: arg.request.id}
            ],
        }),
        patchPreparedCart: build.mutation<IActionResponse, { id: string, request: Partial<IPreparedCart> }>({
            query: (arg) => ({
                url: `/preparedCarts/${arg.id}`,
                method: "PATCH",
                body: arg.request,
            }),
        }),
        deliverPreparedCart: build.mutation<IActionResponse, { id: string, request: IPreparedCartDeliveryRequest }>({
            query: (arg) => ({
                url: `/preparedCarts/${arg.id}/deliver`,
                method: "POST",
                body: arg.request,
            }),
        }),
        getPreparedCart: build.query<IPreparedCart, { workspaceId: string}>({
            query: (arg) => ({
                url: `/preparedCarts/${arg.workspaceId}`,
                method: "GET"
            }),
        }),
        calculatePriceEstimate: build.mutation<IStorefrontBundleEstimate, {storefrontId?: string, workspaceId: string, markupType?: string, markup?: number, minimumPrice?: number, items?: IWorkspaceItem[]}>({
            query: (arg) => ({
                url: `/preparedCarts/${arg.workspaceId}/priceEstimate`,
                method: 'POST',
                body: {
                    markupType: arg.markupType,
                    markup: arg.markup,
                    storefront: arg.storefrontId,
                    minimumPrice: arg.minimumPrice,
                    items: arg.items
                }
            }),
        }),
        generateDirectLinks: build.mutation<IPreparedCartDirectLinksResponse, {workspaceId: string}>({
            query: (arg) => ({
                url: `/preparedCarts/${arg.workspaceId}/directLinks`,
                method: 'POST',
            }),
        }),
        getPreparedCartsForStorefront: build.query<ICollectionResponse<IPreparedCart>, { storefrontId: string, conditions?: ILookupConditions }>({
            query: (arg) => ({
                url: `/storefront/storefronts/${arg.storefrontId}/preparedCarts`,
                method: 'GET',
                params: arg.conditions ? {
                    page: arg.conditions.page,
                    pageSize: arg.conditions.pageSize,
                    searchTerm: arg.conditions.searchTerm,
                    searchBy: arg.conditions.searchBy,
                    sortBy: arg.conditions.sortBy,
                    sortDirection: arg.conditions.sortDirection,
                } : undefined,
            }),
            serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${queryArgs.storefrontId}`,
            forceRefetch({ currentArg, previousArg }) {
                return currentArg !== previousArg;
            },
        }),
        getPreparedCartDeliveryEmailBody: build.query<string, { cartId: string, storefrontId: string}>({
            query: (arg) => ({
                url: `/preparedCarts/${arg.cartId}/storefront/${arg.storefrontId}/email`,
                method: "GET"
            }),
            providesTags: (result, error, arg) => [
                { type: 'PreparedCartEmailBody', id: arg.cartId }
            ],
        })
    }),
});

export const {
    useGetPreparedCartsQuery,
    useSearchPreparedCartRefsQuery,
    useGetPreparedCartsForStorefrontQuery,
    usePutPreparedCartMutation,
    usePatchPreparedCartMutation,
    useGetPreparedCartQuery,
    useDeliverPreparedCartMutation,
    useGetPreparedCartDeliveryEmailBodyQuery,
    useCalculatePriceEstimateMutation,
    useGenerateDirectLinksMutation
} = preparedCartApi;